/**
 * WHAT HAPPENED TO MY MONEY. FR-U-TXN-007 (P1, mandatory).
 *
 * "The system shall let a participant track the status of every transaction in real time."
 *
 * This screen is the audit trail, shown to the person it is about. The backend emits an event at
 * every step of the compliance spine — identity checked, caps reserved, risk scored, escrow locked,
 * ledger posted, escrow released — and they all carry the same correlation id. So the participant can
 * see exactly the same sequence the regulator sees, in the same order, with the same names.
 *
 * That is unusual and it is deliberate. Most payment apps show a status word and hide the machinery,
 * and when something goes wrong the user has nothing to point at. Here, "it stopped at the risk
 * check" is a thing a person can read for themselves and quote to support.
 *
 * The event TYPES are internal names. They are translated below, once, into the same plain language
 * the operator and the regulator see — because a support call where the customer and the agent are
 * reading different words for the same event is a support call that takes three times as long.
 */

import { useCallback, useEffect, useState } from 'react';
import { fmtDateTime } from '@/lib/format';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { api, LipaApiError } from '@/lib/api';
import { ErrorState, LoadingCard } from '@/components/states';

type Movement = Awaited<ReturnType<typeof api.movement>>;

/**
 * Internal event type -> what a person should read.
 *
 * An event with no entry here still renders, using its raw name. Hiding it would be worse: an
 * unexplained step in somebody's payment is exactly the step they most need to see, and a
 * newly-added event type would silently vanish from the trail.
 */
const PLAIN: Record<string, string> = {
  'identity.gate.passed': 'We confirmed you are verified and allowed to transact',
  'governance.cap.reserved': 'We set aside room against your limits',
  'risk.assessed': 'We scored this payment for risk',
  'risk.blocked': 'A risk check stopped this payment',
  'custody.escrow.locked': 'Your money was moved into escrow, protected',
  'txn.ledger.posted': 'The payment was recorded in the ledger',
  'custody.escrow.released': 'The money was released to the recipient',
  'custody.escrow.refunded': 'The money was returned to you',
  'txn.settled': 'Settled',
  'txn.blocked': 'Stopped by a platform control',
  'txn.rolled_back': 'This did not complete, so everything was undone',
};

export default function Activity() {
  // React Router types a param as possibly absent; this route cannot match without one.
  const { correlationId = '' } = useParams();

  const [movement, setMovement] = useState<Movement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setMovement(await api.movement(correlationId));
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not load this payment.');
    } finally {
      setLoading(false);
    }
  }, [correlationId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <LoadingCard lines={5} />;
  if (error) return <ErrorState body={error} onRetry={() => void load()} />;

  if (!movement || movement.events.length === 0) {
    return (
      <ErrorState
        title="We have no record of this"
        body="This reference does not match any payment on your account."
      />
    );
  }

  return (
    <div className="mx-auto max-w-md space-y-5">
      <div>
        <Link to="/wallet" className="text-sm text-brand hover:underline">
          ← Back to wallet
        </Link>
        <h1 className="mt-2 text-page-title text-ink">What happened</h1>
        <p className="mt-1 font-mono text-micro text-ink-muted">{movement.correlationId}</p>
      </div>

      {/*
        Every step, in order, with the time. This is the compliance spine, made visible to the person
        it happened to. It is the same sequence, in the same order, that the regulator's drill-down
        shows — there is not a customer version and a real version.
      */}
      <ol className="relative space-y-0 border-l border-border pl-5">
        {movement.events.map((e, i) => (
          <li key={i} className="relative pb-5 last:pb-0">
            <span
              aria-hidden
              className="absolute -left-[1.4rem] top-1.5 h-2 w-2 rounded-full bg-accent ring-4 ring-canvas"
            />
            <p className="text-sm font-medium text-ink">{PLAIN[e.type] ?? e.type}</p>
            <p className="mt-0.5 text-xs text-ink-muted">{fmtDateTime(e.at)}</p>

            {/* The raw name, quietly. A participant who quotes it to support is quoting the exact
                string an investigator will search the event store for. */}
            <p className="mt-0.5 font-mono text-nano text-ink-muted/70">{e.type}</p>
          </li>
        ))}
      </ol>

      <p className="rounded-card border border-border bg-surface p-4 text-xs leading-relaxed text-ink-muted">
        This is the complete record. Every step of every payment is written to an append-only trail
        that nobody, including us, can edit or delete — which is what makes it worth showing you.
      </p>
    </div>
  );
}
